import React, { useState, useEffect } from "react";
import { BarChart, Bar, ComposedChart, Area, PieChart, Pie, Cell, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { ChevronRight } from "lucide-react";
import { api } from "../utils/api";

const pieColors = ["#3b82f6", "#f59e0b"];

export default function DashboardCharts({ onNavigate }) {
  const [chartData, setChartData] = useState([]);
  const [pieData, setPieData] = useState([{ name: "Aidat", value: 100 }, { name: "Diğer", value: 0 }]);
  const [aidatRatio, setAidatRatio] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Tüm işlemleri çekip hem Gelir hem de Gideri aynı anda hesaplıyoruz
    api.get("/transactions").then(res => {
      const txs = Array.isArray(res.data) ? res.data : [];
      
      const monthNames = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
      const today = new Date();
      const last4Months = [];
      
      for (let i = 3; i >= 0; i--) {
        const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
        last4Months.push({
          year: d.getFullYear(),
          month: d.getMonth(),
          name: `${monthNames[d.getMonth()]}'${d.getFullYear().toString().slice(2)}`,
          gider: 0,
          gelir: 0
        });
      }

      let totalIncome = 0;
      let totalAidat = 0;

      txs.forEach(tx => {
        if (!tx.date) return;
        const txDate = new Date(tx.date);
        const y = txDate.getFullYear();
        const m = txDate.getMonth();
        
        const targetMonth = last4Months.find(x => x.year === y && x.month === m);
        const amount = Number(tx.amount) || 0;

        if (tx.kind === "expense" && targetMonth) {
          targetMonth.gider += amount;
        } else if (tx.kind === "income") {
          if (targetMonth) targetMonth.gelir += amount;
          
          totalIncome += amount;
          if (tx.category === "Aidat ödemeleri") {
            totalAidat += amount;
          }
        }
      });

      const ratio = totalIncome > 0 ? Math.round((totalAidat / totalIncome) * 100) : 0;
      setAidatRatio(ratio);
      setPieData([
        { name: "Aidat", value: ratio },
        { name: "Diğer", value: 100 - ratio }
      ]);

      setChartData(last4Months);
      setLoading(false);
    }).catch(err => {
      console.error("Grafik verileri çekilemedi", err);
      setLoading(false);
    });
  }, []);

  const formatTL = (value) => new Intl.NumberFormat("tr-TR", { style: "currency", currency: "TRY", maximumFractionDigits: 0 }).format(value);

  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: "20px", marginBottom: "30px" }}>
      
      {/* 1. KART: AYLIK GİDERLER */}
      <div style={{ background: "white", borderRadius: "16px", padding: "20px", boxShadow: "0 4px 15px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b" }}>Aylık Giderler</h3>
        </div>
        <div style={{ height: "120px", width: "100%" }}>
          {loading ? (
            <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "13px" }}>Hesaplanıyor...</div>
          ) : (
            <ResponsiveContainer width="99%" height="100%">
              <BarChart data={chartData}>
                <Tooltip formatter={(value) => [formatTL(value), "Gider"]} cursor={{ fill: "transparent" }} contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "14px" }} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} dy={10} />
                <Bar dataKey="gider" fill="url(#colorGider)" radius={[6, 6, 6, 6]} barSize={32} />
                <defs>
                  <linearGradient id="colorGider" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#38bdf8" stopOpacity={1}/>
                    <stop offset="100%" stopColor="#e0f2fe" stopOpacity={0.6}/>
                  </linearGradient>
                </defs>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <button onClick={() => onNavigate("reports_gider")} style={{ display: "flex", alignItems: "center", gap: "4px", background: "none", border: "none", color: "#475569", fontSize: "14px", fontWeight: "600", cursor: "pointer", marginTop: "16px", padding: 0 }}>
          Tümünü Gör <ChevronRight size={16} />
        </button>
      </div>

      {/* 2. KART: AYLIK GELİRLER */}
      <div style={{ background: "white", borderRadius: "16px", padding: "20px", boxShadow: "0 4px 15px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
          <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b" }}>Aylık Gelirler</h3>
        </div>
        <div style={{ height: "120px", width: "100%" }}>
          {loading ? (
            <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "13px" }}>Hesaplanıyor...</div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={chartData}>
                <Tooltip formatter={(value) => [formatTL(value), "Gelir"]} cursor={{ fill: "transparent" }} contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)", fontSize: "14px" }} />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: "#64748b" }} dy={10} />
                
                {/* Kesik Çizgiler */}
                <Bar dataKey="gelir" fill="transparent" stroke="#a855f7" strokeDasharray="3 3" barSize={1} />
                <Area type="monotone" dataKey="gelir" stroke="#a855f7" strokeWidth={3} fill="url(#colorGelir)" />
                <defs>
                  <linearGradient id="colorGelir" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#a855f7" stopOpacity={0.2}/>
                    <stop offset="100%" stopColor="#a855f7" stopOpacity={0}/>
                  </linearGradient>
                </defs>
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </div>
        <button onClick={() => onNavigate("reports_gelir")} style={{ display: "flex", alignItems: "center", gap: "4px", background: "none", border: "none", color: "#475569", fontSize: "14px", fontWeight: "600", cursor: "pointer", marginTop: "16px", padding: 0 }}>
          Tümünü Gör <ChevronRight size={16} />
        </button>
      </div>

      {/* 3. KART: AİDAT GELİRİ ORANI */}
      <div style={{ background: "white", borderRadius: "16px", padding: "20px", boxShadow: "0 4px 15px rgba(0,0,0,0.03)", border: "1px solid #f1f5f9" }}>
        <h3 style={{ margin: 0, fontSize: "16px", color: "#1e293b", marginBottom: "16px" }}>Aidat Geliri Oranı</h3>
        <div style={{ height: "120px", width: "100%", position: "relative" }}>
          {loading ? (
             <div style={{ display: "flex", height: "100%", alignItems: "center", justifyContent: "center", color: "#94a3b8", fontSize: "13px" }}>Hesaplanıyor...</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="100%" startAngle={180} endAngle={0} innerRadius={60} outerRadius={80} paddingAngle={2} dataKey="value" stroke="none">
                    {pieData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={pieColors[index % pieColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`%${value}`, "Oran"]} contentStyle={{ borderRadius: "8px", border: "none", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ position: "absolute", bottom: "10px", left: "0", width: "100%", textAlign: "center", fontSize: "22px", fontWeight: "700", color: "#1e293b" }}>
                %{aidatRatio}
              </div>
            </>
          )}
        </div>
      </div>

    </div>
  );
}